import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { Heading, Text } from "@/components/ui/typography"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { useTranslation } from "react-i18next"

export type RoutesDiagramProps = { id?: string }

const nodeIcons = {
  inflow: sfIcons.createAddress,
  compliance: sfIcons.verify,
  conversion: sfIcons.refresh,
  ledger: sfIcons.database,
  payout: sfIcons.global,
} as const

const nodeIds = ["inflow", "compliance", "conversion", "ledger", "payout"] as const

export function RoutesDiagram({ id }: RoutesDiagramProps) {
  const { t } = useTranslation("marketing")

  const nodes = nodeIds.map((nodeId) => ({
    id: nodeId,
    title: t(`infrastructure.nodes.${nodeId}.title`),
    subtitle: t(`infrastructure.nodes.${nodeId}.subtitle`),
    description: t(`infrastructure.nodes.${nodeId}.description`),
    icon: nodeIcons[nodeId],
  }))

  return (
    <section
      id={id}
      aria-labelledby="routes-diagram-title"
      className={cn(
        "py-12 sm:py-16 md:py-20 lg:py-24",
        semanticTokens.bg.surface
      )}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="space-y-3 sm:space-y-4 text-center md:text-left">
          <Text variant="caption" color="muted" className="uppercase tracking-widest font-medium">
            {t("infrastructure.badge")}
          </Text>
          <Heading level={2} id="routes-diagram-title">
            {t("infrastructure.headline")}
          </Heading>
          <Text variant="lg" color="muted" className="max-w-3xl">
            {t("infrastructure.description")}
          </Text>
        </div>

        <div className="mt-10 sm:mt-12 md:mt-14 lg:mt-16 relative">
          {/* Connecting Line (Desktop) */}
          <div className={cn("hidden md:block absolute top-1/2 left-0 w-full h-0.5 -translate-y-1/2 z-0", semanticTokens.bg.muted)} />

          <div className="grid grid-cols-1 gap-4 sm:gap-5 md:gap-6 md:grid-cols-5 relative z-10">
            {nodes.map((node, index) => {
              const Icon = node.icon || sfIcons.wallet
              if (!Icon) return null
              return (
                <div key={node.id} className="relative group">
                  <SpotlightCard
                    className={cn(
                      "h-full flex flex-col items-center text-center md:items-start md:text-left",
                      // CANON: 240ms standard motion
                      semanticTokens.motion.standard,
                      "transition-all hover:-translate-y-1 hover:shadow-lg",
                      "p-4 sm:p-5"
                    )}
                  >
                    <div className={cn(
                      "inline-flex items-center justify-center rounded-lg mb-3 sm:mb-4 transition-colors",
                      "h-9 w-9 sm:h-10 sm:w-10",
                      "border-2", semanticTokens.border.default, semanticTokens.fg.muted,
                      "group-hover:border-border/80 group-hover:text-foreground"
                    )}>
                      <Icon className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
                    </div>

                    <h3 className={cn(
                      "font-display text-caption-md sm:text-ui-md mb-1", semanticTokens.fg.default
                    )}>
                      {node.title}
                    </h3>
                    <p className={cn(
                      "font-body text-caption-sm font-medium mb-2 sm:mb-3 uppercase tracking-wide",
                      semanticTokens.info.accent
                    )}>
                      {node.subtitle}
                    </p>
                    <p className={cn(
                      "font-body text-caption-sm leading-relaxed", semanticTokens.fg.muted
                    )}>
                      {node.description}
                    </p>
                  </SpotlightCard>

                  {/* Arrow for mobile/desktop flow indication */}
                  {index < nodes.length - 1 && (
                    <div className={cn("absolute -bottom-5 sm:-bottom-6 left-1/2 -translate-x-1/2 md:hidden", semanticTokens.fg.subtle)}>
                      <sfIcons.next className="h-4 w-4 sm:h-5 sm:w-5 rotate-90" />
                    </div>
                  )}
                  {index < nodes.length - 1 && (
                    <div className={cn("hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 z-20 rounded-full p-1", semanticTokens.fg.subtle, semanticTokens.bg.surface, semanticTokens.border.subtle)}>
                      <sfIcons.next className="h-4 w-4" />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
